import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  CREATE_PET_MUTATION,
  PET_BREED_OPTIONS_QUERY,
  LOOKUP_PET_DUPLICATES_QUERY,
  PET_SPECIES_OPTIONS_QUERY,
  UPDATE_PET_MUTATION,
} from './pets.gql';

import { assertMutationSucceeded } from '@/shared/api/graphql-errors';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { PET_SEX_FROM_GQL, PET_SEX_TO_GQL } from '@/shared/graphql/enums';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SmartAutocompleteInput } from '@/shared/ui/SmartAutocompleteInput';

const schema = z.object({
  name: z.string().trim().min(1).max(120),
  species: z.string().trim().max(60).optional().or(z.literal('')),
  breed: z.string().trim().max(80).optional().or(z.literal('')),
  color: z.string().trim().max(60).optional().or(z.literal('')),
  sex: z.enum(['male', 'female', 'unknown']).default('unknown'),
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  approximate_age_months: z.coerce.number().int().min(0).max(600).optional(),
  weight_kg: z.coerce.number().min(0).max(500).optional(),
  notes: z.string().trim().max(2000).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) return null;
  return trimmed;
}

function blankToUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) return undefined;
  return trimmed;
}

function numberToIntOrUndefined(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const number_value = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(number_value)) return undefined;
  return number_value;
}

function numberToIntOrNull(value: unknown): number | null {
  return numberToIntOrUndefined(value) ?? null;
}

function numberToFloatOrUndefined(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const number_value = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number_value)) return undefined;
  return number_value;
}

function numberToFloatOrNull(value: unknown): number | null {
  return numberToFloatOrUndefined(value) ?? null;
}

function gramsToKgOrUndefined(value: number | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  return value / 1000;
}

function kgToGramsIntOrNull(value: unknown): number | null {
  const kg = numberToFloatOrNull(value);
  if (kg === null) return null;
  return Math.round(kg * 1000);
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

function parseIsoDate(value: string): { year: number; month: number; day: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const check = new Date(Date.UTC(year, month - 1, day));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

function calcAgeMonthsFromBirthDate(birth_date: string, today: Date): number | undefined {
  const parsed = parseIsoDate(birth_date);
  if (!parsed) return undefined;
  const today_year = today.getFullYear();
  const today_month = today.getMonth() + 1;
  const today_day = today.getDate();

  let months = (today_year - parsed.year) * 12 + (today_month - parsed.month);
  if (today_day < parsed.day) months -= 1;
  if (months < 0) months = 0;
  return months;
}

function daysInMonth(year: number, month_1_based: number): number {
  return new Date(year, month_1_based, 0).getDate();
}

function calcBirthDateFromAgeMonths(age_months: number, today: Date): string {
  const today_year = today.getFullYear();
  const today_month_zero_based = today.getMonth();
  const today_day = today.getDate();

  const total_months = today_year * 12 + today_month_zero_based - age_months;
  const target_year = Math.floor(total_months / 12);
  const target_month_zero_based = total_months % 12;
  const target_month = target_month_zero_based + 1;
  const target_day = Math.min(today_day, daysInMonth(target_year, target_month));

  return `${target_year}-${pad2(target_month)}-${pad2(target_day)}`;
}

interface PetFormDialogProps {
  mode: 'create' | 'edit';
  customer_id: string;
  initial_pet?: {
    id: string;
    name: string;
    species: string | null;
    breed: string | null;
    color: string | null;
    sex: string;
    birth_date: string | null;
    approximate_age_months: number | null;
    weight_grams: number | null;
    notes: string | null;
  };
  onClose: () => void;
  onSaved: (id?: string) => void | Promise<void>;
}

interface DuplicateMatch {
  pet: { id: string; name: string; species: string | null };
  customer: { id: string; first_name: string; last_name: string | null; phone: string };
  match_score: number;
  match_reason: string;
}

interface SpeciesOption {
  value: string;
  is_system: boolean;
}

export function PetFormDialog({
  mode,
  customer_id,
  initial_pet,
  onClose,
  onSaved,
}: PetFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [submit_error, setSubmitError] = useState<string | null>(null);
  const [pending_breeds, setPendingBreeds] = useState<string[]>([]);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial_pet?.name ?? '',
      species: initial_pet?.species ?? '',
      breed: initial_pet?.breed ?? '',
      color: initial_pet?.color ?? '',
      sex:
        PET_SEX_FROM_GQL[(initial_pet?.sex ?? '') as keyof typeof PET_SEX_FROM_GQL] ??
        'unknown',
      birth_date: initial_pet?.birth_date ?? '',
      approximate_age_months: initial_pet?.approximate_age_months ?? undefined,
      weight_kg: gramsToKgOrUndefined(initial_pet?.weight_grams),
      notes: initial_pet?.notes ?? '',
    },
  });

  const [createPet, { loading: creating }] = useMutation(CREATE_PET_MUTATION);
  const [updatePet, { loading: updating }] = useMutation(UPDATE_PET_MUTATION);
  const [lookupDuplicates, { data }] = useLazyQuery<{
    lookupPetDuplicates: DuplicateMatch[];
  }>(LOOKUP_PET_DUPLICATES_QUERY);
  const { data: species_data } = useQuery<{ petSpeciesOptions: SpeciesOption[] }>(
    PET_SPECIES_OPTIONS_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );

  const watched_name = form.watch('name');
  const watched_species = form.watch('species');
  const watched_age = form.watch('approximate_age_months');
  const birth_date_field = form.register('birth_date');
  const age_months_field = form.register('approximate_age_months');
  const { data: breed_data } = useQuery<{ petBreedOptions: string[] }>(PET_BREED_OPTIONS_QUERY, {
    variables: { species: blankToUndefined(watched_species) ?? null },
    fetchPolicy: 'cache-and-network',
  });

  const species_options = useMemo(() => {
    const api_options = species_data?.petSpeciesOptions ?? [];
    const values = new Set<string>(api_options.map((option) => option.value));
    if (values.size === 0) {
      values.add('canino');
      values.add('felino');
    }
    const current = watched_species?.trim() ?? '';
    if (current.length > 0) values.add(current);
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [species_data?.petSpeciesOptions, watched_species]);

  useEffect(() => {
    if (mode !== 'create') return;
    if (watched_name.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      void lookupDuplicates({
        variables: {
          input: {
            customer_id,
            name: watched_name.trim(),
            species: blankToUndefined(watched_species),
            approximate_age_months: numberToIntOrUndefined(watched_age),
          },
        },
      });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [mode, customer_id, watched_name, watched_species, watched_age, lookupDuplicates]);

  const matches = data?.lookupPetDuplicates ?? [];

  const onSubmitPetForm = async (values: FormValues): Promise<void> => {
    setSubmitError(null);
    const payload = {
      name: values.name.trim(),
      species: blankToNull(values.species),
      breed: blankToNull(values.breed),
      color: blankToNull(values.color),
      sex: PET_SEX_TO_GQL[values.sex],
      birth_date: blankToNull(values.birth_date),
      approximate_age_months: numberToIntOrNull(values.approximate_age_months),
      weight_grams: kgToGramsIntOrNull(values.weight_kg),
      notes: blankToNull(values.notes),
    };

    try {
      if (mode === 'create') {
        const result = await createPet({ variables: { input: { customer_id, ...payload } } });
        assertMutationSucceeded(result, 'create_pet_failed');
        const id = result.data?.createPet?.id;
        await onSaved(id);
      } else if (initial_pet) {
        const result = await updatePet({ variables: { id: initial_pet.id, input: payload } });
        assertMutationSucceeded(result, 'update_pet_failed');
        await onSaved(initial_pet.id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSubmitError(saveErrorBody(message, t));
    }
  };

  const submitting = creating || updating;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-xl">
        <form
          onSubmit={(event) => {
            void form.handleSubmit(onSubmitPetForm)(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle>
              {mode === 'create' ? t('pets.create') : t('pets.edit')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="name">{t('pets.name')}</Label>
                <Input id="name" {...form.register('name')} />
                {form.formState.errors.name ? (
                  <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="species">{t('pets.species')}</Label>
                <select
                  id="species"
                  {...form.register('species')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">{t('pets.species_placeholder')}</option>
                  {species_options.map((value) => (
                    <option key={value} value={value}>
                      {speciesOptionLabel(value)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="breed">{t('pets.breed')}</Label>
                <Controller
                  name="breed"
                  control={form.control}
                  render={({ field }) => (
                    <SmartAutocompleteInput
                      available_values={breed_data?.petBreedOptions ?? []}
                      value={field.value ?? ''}
                      allow_clear
                      placeholder={t('pets.breed_placeholder')}
                      pendingBadgeLabel={t('pets.autocomplete_pending_badge')}
                      addActionLabel={(candidate) =>
                        t('pets.autocomplete_add', { value: candidate })
                      }
                      onPendingValuesChange={setPendingBreeds}
                      onValueChange={(next) => {
                        field.onChange(next);
                      }}
                    />
                  )}
                />
                {mode === 'create' && pending_breeds.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('pets.autocomplete_pending_count', { count: pending_breeds.length })}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="color">{t('pets.color')}</Label>
                <Input id="color" {...form.register('color')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sex">{t('pets.sex')}</Label>
                <select
                  id="sex"
                  {...form.register('sex')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="unknown">{t('pets.sex_unknown')}</option>
                  <option value="male">{t('pets.sex_male')}</option>
                  <option value="female">{t('pets.sex_female')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="birth_date">{t('pets.birth_date')}</Label>
                <Input
                  id="birth_date"
                  type="date"
                  {...birth_date_field}
                  onChange={(event) => {
                    void birth_date_field.onChange(event);
                    const next_birth_date = event.target.value;
                    if (next_birth_date.trim().length === 0) {
                      form.setValue('approximate_age_months', undefined, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      return;
                    }
                    const months = calcAgeMonthsFromBirthDate(next_birth_date, new Date());
                    form.setValue('approximate_age_months', months, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="approximate_age_months">{t('pets.age_months')}</Label>
                <Input
                  id="approximate_age_months"
                  type="number"
                  min={0}
                  max={600}
                  {...age_months_field}
                  onChange={(event) => {
                    void age_months_field.onChange(event);
                    const raw = event.target.value.trim();
                    if (raw.length === 0) {
                      form.setValue('birth_date', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      return;
                    }
                    const parsed = Number(raw);
                    if (!Number.isInteger(parsed) || parsed < 0) return;
                    const birth_date = calcBirthDateFromAgeMonths(parsed, new Date());
                    form.setValue('birth_date', birth_date, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight_kg">{t('pets.weight_kg')}</Label>
                <Input
                  id="weight_kg"
                  type="number"
                  step="0.001"
                  min={0}
                  max={500}
                  {...form.register('weight_kg')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">{t('pets.notes')}</Label>
              <textarea
                id="notes"
                rows={3}
                {...form.register('notes')}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {mode === 'create' && matches.length > 0 ? (
              <div className="rounded-md border border-warning/40 bg-warning/15 p-3 text-sm">
                <p className="font-medium text-warning">
                  {t('pets.duplicate_warning')}
                </p>
                <ul className="mt-2 space-y-1 text-warning">
                  {matches.slice(0, 5).map((m) => (
                    <li key={m.pet.id} className="flex items-center justify-between gap-2">
                      <span>
                        {m.pet.name} {m.pet.species ? `(${m.pet.species})` : ''} —{' '}
                        {m.customer.first_name}
                        {m.customer.last_name ? ` ${m.customer.last_name}` : ''}
                      </span>
                      <Badge variant="outline">
                        {Math.round(m.match_score * 100)}%
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {submit_error ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <p className="font-medium">{t('pets.save_error_title')}</p>
                <p>{submit_error}</p>
              </div>
            ) : null}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

function saveErrorBody(
  message: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (/pet_species_not_allowed/i.test(message)) return t('pets.save_error_species_not_allowed');
  if (/PetSex|sex|enum/i.test(message)) return t('pets.save_error_sex');
  if (/customer|cliente/i.test(message)) return t('pets.save_error_customer');
  if (/forbidden|permission|unauthorized|missing_bearer/i.test(message)) {
    return t('pets.save_error_permission');
  }
  return t('pets.save_error_body');
}

function speciesOptionLabel(value: string): string {
  if (value === 'canino') return 'Canino';
  if (value === 'felino') return 'Felino';
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
